import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt, faTrophy, faChartLine, faExclamationTriangle, faArrowLeft,
  faUsers, faFire, faInfoCircle, faTableCells, faList,
} from '@fortawesome/free-solid-svg-icons';
import { AREA_CONFIG, getLevelInfo } from './LevelUpHubPage';
import api from '../api/API';
import './TeamPDIPage.css';

const AVATAR_COLORS = [
  '#6366f1', '#ec4899', '#8b5cf6', '#f59e0b', '#10b981',
  '#3b82f6', '#ef4444', '#14b8a6', '#f97316', '#a855f7',
];

const getAvatarColor = (str) => {
  const idx = String(str).split('').reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
};

const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const daysAgoFromDate = (dateStr) => {
  if (!dateStr) return 999;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
};

const TeamPDIPage = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');

  const workspaceId = localStorage.getItem('selectedWorkspaceId');

  useEffect(() => {
    if (!workspaceId) { setLoading(false); return; }
    api.get(`/workspaces/${workspaceId}/team-pdi`)
      .then(res => setMembers(res?.data?.records || []))
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  // Enriquece cada membro com dados vindos da API real
  const enrichedMembers = useMemo(() =>
    members.map(m => {
      const id = m.id_usuario ?? m.id;
      const areaXp = m.xp_areas || 0;
      const daysAgo = daysAgoFromDate(m.ultima_atividade);
      const pdi = {
        xp: areaXp,
        goalCount: m.metas_ativas || 0,
        skillCount: (m.skills || []).length,
        daysAgo,
        goalProgress: parseFloat(m.progresso_medio || 0),
      };
      const levelInfo = getLevelInfo(areaXp);
      const color = getAvatarColor(id);
      const name = m.nome || m.email || 'Membro';
      return { ...m, id, name, color, pdi, skills: m.skills || [], levelInfo };
    }),
    [members]
  );

  const alerts = useMemo(() =>
    enrichedMembers.filter(m => m.pdi.daysAgo >= 7 || m.pdi.goalCount === 0),
    [enrichedMembers]
  );

  const avgLevel = useMemo(() => {
    if (!enrichedMembers.length) return 0;
    const sum = enrichedMembers.reduce((a, m) => a + m.levelInfo.current.level, 0);
    return (sum / enrichedMembers.length).toFixed(1);
  }, [enrichedMembers]);

  const totalXp = useMemo(() =>
    enrichedMembers.reduce((a, m) => a + m.pdi.xp, 0),
    [enrichedMembers]
  );

  const totalGoals = useMemo(() =>
    enrichedMembers.reduce((a, m) => a + m.pdi.goalCount, 0),
    [enrichedMembers]
  );

  const leaderboard = useMemo(() =>
    [...enrichedMembers].sort((a, b) => b.pdi.xp - a.pdi.xp).slice(0, 6),
    [enrichedMembers]
  );

  // Agrupa skills por área para o mapa de skills do time
  const teamSkillsMap = useMemo(() => {
    const map = {};
    enrichedMembers.forEach(m => {
      m.skills.forEach(skill => {
        const key = `${skill.nome}|${skill.area}`;
        if (!map[key]) map[key] = { nome: skill.nome, area: skill.area, count: 0 };
        map[key].count++;
      });
    });
    const grouped = {};
    Object.values(map).forEach(s => {
      if (!grouped[s.area]) grouped[s.area] = [];
      grouped[s.area].push(s);
    });
    Object.keys(grouped).forEach(k => grouped[k].sort((a, b) => b.count - a.count));
    return grouped;
  }, [enrichedMembers]);

  const getStatusInfo = (pdi) => {
    if (pdi.daysAgo >= 10) return { label: 'Inativo', color: '#ef4444', bg: 'rgba(239,68,68,0.15)' };
    if (pdi.daysAgo >= 7)  return { label: 'Atenção', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' };
    return { label: 'Ativo', color: '#10b981', bg: 'rgba(16,185,129,0.15)' };
  };

  const rankStyle = (i) => {
    if (i === 0) return 'gold';
    if (i === 1) return 'silver';
    if (i === 2) return 'bronze';
    return 'other';
  };

  if (!workspaceId) {
    return (
      <div className="tpdi-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'rgba(226,232,240,0.5)' }}>
          <FontAwesomeIcon icon={faUsers} style={{ fontSize: '3rem', marginBottom: 16, opacity: 0.3 }} />
          <p>Selecione um workspace para ver o Time PDI.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tpdi-page">
      {/* Header */}
      <div className="tpdi-header">
        <div className="tpdi-header-left">
          <button
            style={{ background: 'transparent', border: 'none', color: 'rgba(226,232,240,0.5)', cursor: 'pointer', marginBottom: 8, fontSize: '0.84rem' }}
            onClick={() => navigate('/levelup')}
          >
            <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 6 }} />
            LevelUp Hub
          </button>
          <h1>Time PDI</h1>
          <div className="tpdi-header-meta">
            <span>{enrichedMembers.length} membros</span>
            <span>·</span>
            <span className="tpdi-beta-badge">
              <FontAwesomeIcon icon={faBolt} />
              BETA
            </span>
          </div>
        </div>
        <div className="tpdi-header-actions">
          <div className="tpdi-view-toggle">
            <button
              className={`tpdi-view-btn${viewMode === 'grid' ? ' active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <FontAwesomeIcon icon={faTableCells} style={{ marginRight: 5 }} />
              Cards
            </button>
            <button
              className={`tpdi-view-btn${viewMode === 'list' ? ' active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <FontAwesomeIcon icon={faList} style={{ marginRight: 5 }} />
              Lista
            </button>
          </div>
        </div>
      </div>

      {/* Transparency notice */}
      <div className="tpdi-beta-notice">
        <FontAwesomeIcon icon={faInfoCircle} className="tpdi-beta-notice-icon" />
        <span>
          Dados reais de cada membro do workspace. Membros com <strong style={{ color: '#a5b4fc' }}>0 XP ou 0 metas</strong> ainda não configuraram seu PDI no LevelUp Hub.
          Todos os membros sabem que o gestor tem visibilidade sobre sua evolução.
        </span>
      </div>

      {/* Alerts banner */}
      {alerts.length > 0 && (
        <div className="tpdi-alerts-banner">
          <div className="tpdi-alerts-icon">
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </div>
          <div className="tpdi-alerts-text">
            <strong>{alerts.length} membro{alerts.length > 1 ? 's' : ''} precisam de atenção</strong>
            <span>Sem atividade nos últimos 7+ dias ou sem metas definidas</span>
          </div>
          <div className="tpdi-alert-chips">
            {alerts.slice(0, 4).map(m => (
              <span key={m.id} className="tpdi-alert-chip">{m.name.split(' ')[0]}</span>
            ))}
            {alerts.length > 4 && <span className="tpdi-alert-chip">+{alerts.length - 4}</span>}
          </div>
        </div>
      )}

      {/* Metrics */}
      <div className="tpdi-metrics">
        <div className="tpdi-metric accent-purple">
          <div className="tpdi-metric-label">Nível Médio do Time</div>
          <div className="tpdi-metric-value">{avgLevel}</div>
          <div className="tpdi-metric-sub">de 10 possível</div>
        </div>
        <div className="tpdi-metric accent-gold">
          <div className="tpdi-metric-label">XP Total Acumulado</div>
          <div className="tpdi-metric-value">{(totalXp / 1000).toFixed(1)}k</div>
          <div className="tpdi-metric-sub">{totalXp.toLocaleString('pt-BR')} XP</div>
        </div>
        <div className="tpdi-metric accent-green">
          <div className="tpdi-metric-label">Metas Ativas</div>
          <div className="tpdi-metric-value">{totalGoals}</div>
          <div className="tpdi-metric-sub">em andamento</div>
        </div>
        <div className="tpdi-metric accent-red">
          <div className="tpdi-metric-label">Precisam de Atenção</div>
          <div className="tpdi-metric-value">{alerts.length}</div>
          <div className="tpdi-metric-sub">{alerts.length === 0 ? 'time em dia ✓' : 'fale com eles'}</div>
        </div>
      </div>

      {/* Main: members + leaderboard */}
      <div className="tpdi-main">
        {loading ? (
          <p style={{ color: 'rgba(226,232,240,0.4)', gridColumn: '1/-1' }}>Carregando membros...</p>
        ) : (
          <div className="tpdi-members-grid">
            {enrichedMembers.map(member => {
              const status = getStatusInfo(member.pdi);
              return (
                <div
                  key={member.id}
                  className="tpdi-member-card"
                  style={{ '--status-color': status.color }}
                >
                  <div className="tpdi-member-top">
                    <div className="tpdi-avatar" style={{ background: member.color }}>
                      {getInitials(member.name)}
                    </div>
                    <div className="tpdi-member-info">
                      <p className="tpdi-member-name">{member.name}</p>
                      <span className="tpdi-member-role">{member.email || 'Membro do time'}</span>
                    </div>
                    <div
                      className="tpdi-level-chip"
                      style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', color: '#a5b4fc' }}
                    >
                      <FontAwesomeIcon icon={faBolt} style={{ fontSize: '0.68rem' }} />
                      Lv {member.levelInfo.current.level}
                    </div>
                  </div>

                  <div className="tpdi-card-xp-bar">
                    <div className="tpdi-card-xp-fill" style={{ width: `${member.levelInfo.pct}%` }} />
                  </div>
                  <div className="tpdi-card-xp-label">
                    <span>{member.levelInfo.current.title}</span>
                    <span>{member.pdi.xp.toLocaleString('pt-BR')} XP</span>
                  </div>

                  <div className="tpdi-card-stats">
                    <div className="tpdi-card-stat">
                      <FontAwesomeIcon icon={faTrophy} className="tpdi-card-stat-icon" style={{ color: '#f59e0b' }} />
                      {member.pdi.goalCount} metas ativas
                    </div>
                    <div className="tpdi-card-stat">
                      <FontAwesomeIcon icon={faChartLine} className="tpdi-card-stat-icon" style={{ color: '#10b981' }} />
                      {member.pdi.skillCount} skills
                    </div>
                    <div className="tpdi-card-stat">
                      <FontAwesomeIcon icon={faFire} className="tpdi-card-stat-icon" style={{ color: '#f97316' }} />
                      {member.pdi.daysAgo === 0 ? 'hoje' : `${member.pdi.daysAgo}d atrás`}
                    </div>
                  </div>

                  <div
                    className="tpdi-status-chip"
                    style={{ background: status.bg, border: `1px solid ${status.color}44`, color: status.color }}
                  >
                    <div className="tpdi-status-dot" style={{ background: status.color }} />
                    {status.label}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Right panel */}
        <div className="tpdi-right-panel">
          <div className="tpdi-panel">
            <div className="tpdi-panel-title">
              <FontAwesomeIcon icon={faTrophy} style={{ color: '#f59e0b' }} />
              Ranking XP
            </div>
            <div className="tpdi-leaderboard-list">
              {leaderboard.map((m, i) => (
                <div key={m.id} className="tpdi-lb-row">
                  <span className={`tpdi-lb-rank ${rankStyle(i)}`}>
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}º`}
                  </span>
                  <div className="tpdi-lb-avatar" style={{ background: m.color }}>
                    {getInitials(m.name)}
                  </div>
                  <span className="tpdi-lb-name">{m.name.split(' ')[0]}</span>
                  <span className="tpdi-lb-xp">{m.pdi.xp.toLocaleString('pt-BR')}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="tpdi-panel">
            <div className="tpdi-panel-title">
              <FontAwesomeIcon icon={faUsers} style={{ color: '#6366f1' }} />
              Status do Time
            </div>
            {['Ativo', 'Atenção', 'Inativo'].map(label => {
              const colorMap = { Ativo: '#10b981', Atenção: '#f59e0b', Inativo: '#ef4444' };
              const count = enrichedMembers.filter(m => getStatusInfo(m.pdi).label === label).length;
              const pct = enrichedMembers.length ? Math.round((count / enrichedMembers.length) * 100) : 0;
              return (
                <div key={label} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontSize: '0.82rem', color: colorMap[label] }}>{label}</span>
                    <span style={{ fontSize: '0.82rem', color: 'rgba(226,232,240,0.45)' }}>{count} ({pct}%)</span>
                  </div>
                  <div style={{ height: 5, borderRadius: 99, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: colorMap[label], borderRadius: 99, transition: 'width 0.7s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Skills map */}
      {Object.keys(teamSkillsMap).length > 0 && (
        <div className="tpdi-skills-section">
          <div className="tpdi-skills-section-title">
            <FontAwesomeIcon icon={faChartLine} style={{ color: '#10b981' }} />
            Mapa de Habilidades do Time
          </div>
          {AREA_CONFIG.map(area => {
            const areaSkills = teamSkillsMap[area.id];
            if (!areaSkills?.length) return null;
            return (
              <div key={area.id} className="tpdi-skills-area-group">
                <div className="tpdi-skills-area-label">
                  <FontAwesomeIcon icon={area.icon} style={{ color: area.color }} />
                  {area.label}
                </div>
                <div className="tpdi-skills-tags">
                  {areaSkills.map((skill, i) => (
                    <div
                      key={i}
                      className="tpdi-skill-tag"
                      style={{
                        background: `${area.color}18`,
                        border: `1px solid ${area.color}33`,
                        color: area.color,
                      }}
                    >
                      {skill.nome}
                      {skill.count > 1 && (
                        <span className="tpdi-skill-count">{skill.count}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TeamPDIPage;
